import { forwardRef, Ref, useCallback, useRef, useState } from "react";
import cx from "classnames";

import { TableHeaderCell } from "../../../base/cells/TableHeaderCell";
import { HeaderContext } from "@tanstack/react-table";
import { DataTableHeaderCellProps as BaseDataTableHeaderCellProps } from "../../../base/cells/TableHeaderCell";
import { SrOnly } from "../../../../../../components";

import styles from "./DataTableCells.module.scss";
import { useDTFocusDispatchContext } from "../context/focus/useDTFocusDispatchContext";
import { formatCellPositionString } from "../util/cellPositionHelpers";
import {
  INTERNAL_ROW_ACTION_PRIMARY_COLUMN_ID,
  INTERNAL_ROW_ACTION_SECONDARY_COLUMN_ID,
} from "../constants";

const INTERACTIVE_DESCENDANT_SELECTOR = [
  "button",
  "a[href]",
  "input",
  "select",
  "textarea",
  '[role="button"]',
  '[role="link"]',
  '[contenteditable="true"]',
].join(", ");

function isInteractiveDescendant(
  target: EventTarget | null,
  currentTarget: HTMLElement,
) {
  if (!(target instanceof Element)) {
    return false;
  }

  const interactiveElement = target.closest(INTERACTIVE_DESCENDANT_SELECTOR);

  return !!interactiveElement && currentTarget.contains(interactiveElement);
}

/**
 * Props for the DataTableHeaderCell component
 * @extends BaseDataTableHeaderCellProps
 */
export type DataTableHeaderCellProps<T, TValue> =
  BaseDataTableHeaderCellProps & {
    cellPosition: { rowIndex: number; columnIndex: number };
    header: HeaderContext<T, TValue>;
  };

/**
 * Data table header cell with focus management
 * @param props - The props for the DataTableHeaderCell component
 * @param props.cellPosition - The cell position of the cell
 * @param props.children - The children of the DataTableHeaderCell component
 * @param props.header - The header of the DataTableHeaderCell component
 * @param props.isFocused - Whether the cell is focused
 */
function DataTableHeaderCellInner<T, TValue>(
  props: DataTableHeaderCellProps<T, TValue>,
  forwardedRef: Ref<HTMLDivElement>,
) {
  const { cellPosition, children, header, className, ...rest } = props;

  const internalRef = useRef<HTMLDivElement>(null);

  // Merge internal and forwarded refs
  const setRefs = useCallback(
    (element: HTMLDivElement | null) => {
      // Set internal ref
      (internalRef as React.MutableRefObject<HTMLDivElement | null>).current =
        element;
      // Set forwarded ref
      if (typeof forwardedRef === "function") {
        forwardedRef(element);
      } else if (forwardedRef) {
        (
          forwardedRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = element;
      }
    },
    [forwardedRef],
  );

  const { moveFocusToCell } = useDTFocusDispatchContext();
  const [tabIndex, setTabIndex] = useState(-1);
  const handleFocus = useCallback(() => {
    moveFocusToCell(cellPosition);
    setTabIndex(0);
  }, [moveFocusToCell, cellPosition, setTabIndex]);

  const handleBlur = useCallback(() => {
    setTabIndex(-1);
  }, [setTabIndex]);

  const { jumpFocus, moveFocus } = useDTFocusDispatchContext();

  // Derive cell action from column ID
  const columnId = header.column.id;
  const isExpandCell = columnId === INTERNAL_ROW_ACTION_PRIMARY_COLUMN_ID;
  const isSelectCell = columnId === INTERNAL_ROW_ACTION_SECONDARY_COLUMN_ID;
  const isSortingCell = header.column.getCanSort();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      switch (event.key) {
        case "ArrowUp":
          event.preventDefault();
          moveFocus("up");
          return;
        case "ArrowDown":
          event.preventDefault();
          moveFocus("down");
          return;
        case "ArrowLeft":
          event.preventDefault();
          moveFocus("left");
          return;
        case "ArrowRight":
          event.preventDefault();
          moveFocus("right");
          return;
        case "Home": {
          event.preventDefault();
          jumpFocus("left");
          return;
        }
        case "End": {
          event.preventDefault();
          jumpFocus("right");
          return;
        }
        case " ":
        case "Enter": {
          if (isInteractiveDescendant(event.target, event.currentTarget)) {
            return;
          }

          const isDisabled = header.column.columnDef.meta?.disableHeaderAction;
          if (isSelectCell && !isDisabled) {
            event.preventDefault();
            header.table.toggleAllPageRowsSelected();
            return;
          }
          if (isExpandCell && !isDisabled) {
            event.preventDefault();
            header.table.toggleAllRowsExpanded();
            return;
          }
          if (isSortingCell) {
            event.preventDefault();
            header.column.toggleSorting();
            return;
          }
          return;
        }
        default:
          break;
      }
    },
    [
      moveFocus,
      jumpFocus,
      header.column,
      header.table,
      isExpandCell,
      isSelectCell,
      isSortingCell,
    ],
  );

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isSortingCell) {
        if (!(event.target instanceof Element)) {
          return;
        }

        const resizeHandle = event.target.closest(
          '[aria-roledescription="Resize handle"]',
        );

        if (
          resizeHandle ||
          isInteractiveDescendant(event.target, event.currentTarget)
        ) {
          return;
        }

        header.column.getToggleSortingHandler()?.(event);
      }
    },
    [header.column, isSortingCell],
  );

  return (
    <TableHeaderCell
      type="data-table"
      {...rest}
      ref={setRefs}
      tabIndex={tabIndex}
      onFocus={handleFocus}
      onClick={handleClick}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={cx(styles["data-table-header-cell"], className)}
      data-cell-pos={formatCellPositionString(cellPosition)}
      aria-sort={
        header.column.getIsSorted() === "asc"
          ? "ascending"
          : header.column.getIsSorted() === "desc"
            ? "descending"
            : undefined
      }
    >
      {children}
      {isSortingCell && (
        <SrOnly>
          {header.column.getIsSorted() === "asc"
            ? "Sorted ascending."
            : header.column.getIsSorted() === "desc"
              ? "Sorted descending."
              : "Not sorted."}{" "}
          Click to toggle sort.
        </SrOnly>
      )}
    </TableHeaderCell>
  );
}

/**
 * Data table header cell with focus management and ref forwarding
 */
export const DataTableHeaderCell = forwardRef(DataTableHeaderCellInner) as <
  T,
  TValue,
>(
  props: DataTableHeaderCellProps<T, TValue> & { ref?: Ref<HTMLDivElement> },
) => ReturnType<typeof DataTableHeaderCellInner>;
